import { useState, useEffect } from 'react';
import {
  X, Monitor, Smartphone, Tablet, Clock, LogOut, AlertTriangle,
  Loader2, RefreshCw, Shield, Trash2
} from 'lucide-react';

const AdminUserSessionsModal = ({ user, onClose }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);
  const [showRevokeAllConfirm, setShowRevokeAllConfirm] = useState(false);
  const [revokeReason, setRevokeReason] = useState('');

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/sessions/admin/users/${user.id}/sessions`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setSessions(data.sessions);
      }
    } catch (err) {
      console.error('Failed to load sessions:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchSessions();
    }
  }, [user]);

  const handleRevokeSession = async (sessionId) => {
    const reason = prompt('Reason for revoking this session:', 'Security Concern');
    if (!reason) return;

    setRevoking(sessionId);
    try {
      const response = await fetch(`/api/sessions/admin/${sessionId}/revoke`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason })
      });
      const data = await response.json();
      if (data.success) {
        setSessions(sessions.filter(s => s.id !== sessionId));
      } else {
        alert(data.error || 'Failed to revoke session');
      }
    } catch (err) {
      alert('Failed to revoke session');
    } finally {
      setRevoking(null);
    }
  };

  const handleRevokeAll = async () => {
    if (!revokeReason) {
      alert('Please select a reason');
      return;
    }

    setRevoking('all');
    try {
      const response = await fetch(`/api/sessions/admin/users/${user.id}/revoke-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason: revokeReason })
      });
      const data = await response.json();
      if (data.success) {
        setSessions([]);
        setShowRevokeAllConfirm(false);
        alert(`${data.count} session(s) have been revoked.`);
      } else {
        alert(data.error || 'Failed to revoke sessions');
      }
    } catch (err) {
      alert('Failed to revoke sessions');
    } finally {
      setRevoking(null);
    }
  };

  const getDeviceIcon = (device) => {
    switch (device?.type) {
      case 'Mobile': return <Smartphone size={18} />;
      case 'Tablet': return <Tablet size={18} />;
      default: return <Monitor size={18} />;
    }
  };

  const getTimeAgo = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const diffMs = new Date() - new Date(dateStr);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!user) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'var(--bg-card, #161f2d)',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '600px',
          maxHeight: '80vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid var(--border-color, #333)'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid var(--border-color, #333)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Shield size={20} color="var(--accent-primary, #00d4aa)" />
              Active Sessions
            </h2>
            <p style={{ margin: '4px 0 0', color: 'var(--text-secondary)', fontSize: '14px' }}>
              {user.name || user.email} ({user.email})
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              padding: '8px'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Actions Bar */}
        <div style={{
          padding: '12px 20px',
          borderBottom: '1px solid var(--border-color, #333)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: 'rgba(0,0,0,0.2)'
        }}>
          <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
            {sessions.length} active session{sessions.length !== 1 ? 's' : ''}
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={fetchSessions}
              disabled={loading}
              style={{
                padding: '6px 10px',
                backgroundColor: 'transparent',
                border: '1px solid #444',
                borderRadius: '6px',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '12px'
              }}
            >
              <RefreshCw size={12} className={loading ? 'spin' : ''} />
              Refresh
            </button>
            {sessions.length > 0 && (
              <button
                onClick={() => setShowRevokeAllConfirm(true)}
                style={{
                  padding: '6px 10px',
                  backgroundColor: 'rgba(239, 68, 68, 0.15)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '6px',
                  color: '#f87171',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '12px'
                }}
              >
                <Trash2 size={12} />
                Revoke All
              </button>
            )}
          </div>
        </div>

        {/* Sessions List */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
              <Loader2 size={24} className="spin" />
              <p style={{ marginTop: '12px' }}>Loading sessions...</p>
            </div>
          ) : sessions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
              <Monitor size={32} style={{ opacity: 0.5, marginBottom: '12px' }} />
              <p>No active sessions</p>
              <p style={{ fontSize: '13px' }}>This user is not currently logged in anywhere.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {sessions.map((session) => (
                <div
                  key={session.id}
                  style={{
                    padding: '14px 16px',
                    backgroundColor: 'var(--bg-secondary, #111820)',
                    border: '1px solid var(--border-color, #333)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}
                >
                  <div style={{
                    padding: '10px',
                    backgroundColor: 'var(--bg-tertiary, #1a2332)',
                    borderRadius: '8px',
                    color: 'var(--text-secondary)'
                  }}>
                    {getDeviceIcon(session.device)}
                  </div>

                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                      <span style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: '500' }}>
                        {session.device?.browser || 'Unknown'} on {session.device?.os || 'Unknown'}
                      </span>
                      {session.isExtended && (
                        <span style={{
                          padding: '2px 6px',
                          backgroundColor: 'rgba(245, 158, 11, 0.15)',
                          color: '#f59e0b',
                          borderRadius: '4px',
                          fontSize: '10px'
                        }}>
                          30-day session
                        </span>
                      )}
                    </div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
                      <Clock size={10} style={{ display: 'inline', marginRight: '4px' }} />
                      Last active: {getTimeAgo(session.lastActivityAt)}
                      {session.ipAddress && ` • IP: ${session.ipAddress}`}
                    </div>
                    <div style={{ color: 'var(--text-tertiary)', fontSize: '11px', marginTop: '2px' }}>
                      Created: {formatDate(session.createdAt)} • Expires: {formatDate(session.expiresAt)}
                    </div>
                  </div>

                  <button
                    onClick={() => handleRevokeSession(session.id)}
                    disabled={revoking === session.id}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: 'transparent',
                      border: '1px solid #dc2626',
                      borderRadius: '6px',
                      color: '#f87171',
                      cursor: 'pointer',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    {revoking === session.id ? (
                      <Loader2 size={12} className="spin" />
                    ) : (
                      <LogOut size={12} />
                    )}
                    Revoke
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Revoke All Confirmation */}
        {showRevokeAllConfirm && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001
          }}>
            <div style={{
              backgroundColor: 'var(--bg-card, #161f2d)',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              border: '1px solid var(--border-color, #333)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <AlertTriangle size={24} color="#f59e0b" />
                <h3 style={{ margin: 0, color: 'var(--text-primary)' }}>Emergency Lockout</h3>
              </div>

              <p style={{ color: 'var(--text-secondary)', marginBottom: '16px', fontSize: '14px' }}>
                This will immediately terminate all {sessions.length} active session{sessions.length !== 1 ? 's' : ''} for <strong>{user.name || user.email}</strong>.
                They will be logged out of all devices.
              </p>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '6px' }}>
                  Reason for revocation *
                </label>
                <select
                  value={revokeReason}
                  onChange={(e) => setRevokeReason(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    backgroundColor: 'var(--bg-secondary, #111820)',
                    border: '1px solid #444',
                    borderRadius: '6px',
                    color: 'var(--text-primary)',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Select a reason...</option>
                  <option value="Device Lost">Device Lost</option>
                  <option value="Device Stolen">Device Stolen</option>
                  <option value="Security Concern">Security Concern</option>
                  <option value="Employee Terminated">Employee Terminated</option>
                  <option value="Account Compromised">Account Compromised</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowRevokeAllConfirm(false);
                    setRevokeReason('');
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: 'transparent',
                    border: '1px solid #444',
                    borderRadius: '6px',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleRevokeAll}
                  disabled={!revokeReason || revoking === 'all'}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: revokeReason ? '#dc2626' : '#333',
                    border: 'none',
                    borderRadius: '6px',
                    color: revokeReason ? '#fff' : '#666',
                    cursor: revokeReason ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {revoking === 'all' ? <Loader2 size={14} className="spin" /> : <AlertTriangle size={14} />}
                  Revoke All Sessions
                </button>
              </div>
            </div>
          </div>
        )}

        {/* CSS for spin animation */}
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .spin {
            animation: spin 1s linear infinite;
          }
        `}</style>
      </div>
    </div>
  );
};

export default AdminUserSessionsModal;
